import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Assessment, Download, EventNote, MonitorHeart, PlayCircle, Search } from '@mui/icons-material';
import { Link } from 'react-router-dom';

import { downloadBlobFile, ReportEmptyState, ReportsNavigation } from '../../components/reports/ReportFilters';
import reportsService, {
  ContentPlaybackReport,
  DeviceUptimeReport,
  MonitoringResponse,
  ReportTypeDefinition,
  SystemActivityReport,
} from '../../services/reportsService';

const routeByReportType: Record<string, string> = {
  device_uptime: '/reports/devices-uptime',
  content_playback: '/reports/content-playback',
  system_activity: '/reports/system-activity',
  custom: '/reports',
};

const todayIso = () => new Date().toISOString();
const monthAgoIso = () => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

const numberFormat = new Intl.NumberFormat('ru-RU');

const formatNumber = (value?: number | null) => numberFormat.format(value || 0);
const formatPercent = (value?: number | null) => `${Number(value || 0).toFixed(1)}%`;

const formatDuration = (seconds?: number | null) => {
  const total = Math.max(0, Math.round(seconds || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours} ч ${minutes} мин`;
  if (minutes > 0) return `${minutes} мин`;
  return `${total} сек`;
};

const MetricCell: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  caption?: React.ReactNode;
  progress?: number;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
}> = ({ icon, label, value, caption, progress, color = 'primary' }) => (
  <Paper
    elevation={1}
    sx={{
      p: 1,
      borderRadius: 1.25,
      height: '100%',
      minWidth: 0,
      borderLeft: 4,
      borderColor: `${color}.main`,
    }}
  >
    <Stack direction="row" spacing={0.85} alignItems="flex-start">
      <Box sx={{ display: 'flex', color: `${color}.main`, mt: 0.2 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
        <Typography variant="h6" sx={{ lineHeight: 1.1, fontWeight: 900 }}>{value}</Typography>
        {caption && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
            {caption}
          </Typography>
        )}
      </Box>
    </Stack>
    {typeof progress === 'number' && (
      <LinearProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, progress))}
        sx={{ height: 4, borderRadius: 1, mt: 0.75 }}
        color={color === 'primary' ? 'primary' : color}
      />
    )}
  </Paper>
);

const ReportsPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [reportTypes, setReportTypes] = useState<ReportTypeDefinition[]>([]);
  const [monitoring, setMonitoring] = useState<MonitoringResponse | null>(null);
  const [deviceUptime, setDeviceUptime] = useState<DeviceUptimeReport[]>([]);
  const [contentPlayback, setContentPlayback] = useState<ContentPlaybackReport[]>([]);
  const [systemActivity, setSystemActivity] = useState<SystemActivityReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const period = { date_from: monthAgoIso(), date_to: todayIso() };
        const [types, monitoringData, uptimeData, playbackData, activityData] = await Promise.all([
          reportsService.getReportTypes(),
          reportsService.getMonitoring(),
          reportsService.getDevicesUptime(period),
          reportsService.getContentPlayback(period),
          reportsService.getSystemActivity(period),
        ]);

        setReportTypes(types.report_types);
        setMonitoring(monitoringData);
        setDeviceUptime(uptimeData.data);
        setContentPlayback(playbackData.data);
        setSystemActivity(activityData.data);
        setError(null);
      } catch (err: any) {
        setError(err?.detail || err?.message || 'Не удалось загрузить отчеты');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const filteredTypes = useMemo(
    () => reportTypes.filter((report) => {
      const query = search.toLowerCase();
      return report.name.toLowerCase().includes(query) || report.description.toLowerCase().includes(query);
    }),
    [reportTypes, search],
  );

  const totalPlaybacks = contentPlayback
    .filter((item) => item.content_type === 'media')
    .reduce((sum, item) => sum + item.total_plays, 0);
  const totalPlaybackSeconds = contentPlayback.reduce(
    (sum, item) => sum + (item.total_duration_seconds || item.total_duration || 0),
    0,
  );
  const averageUptime = deviceUptime.length
    ? deviceUptime.reduce((sum, item) => sum + item.uptime_percentage, 0) / deviceUptime.length
    : 0;
  const totalEvents = systemActivity.reduce((sum, item) => sum + item.total_events, 0);
  const errors = monitoring?.activity.errors_24h ?? systemActivity.reduce((sum, item) => sum + item.errors, 0);
  const worstUptime = [...deviceUptime].sort((a, b) => a.uptime_percentage - b.uptime_percentage).slice(0, 8);
  const topContent = [...contentPlayback].sort((a, b) => b.total_plays - a.total_plays).slice(0, 8);
  const activityRows = [...systemActivity].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 8);

  const exportAllExcel = async () => {
    try {
      setExporting(true);
      const file = await reportsService.exportReport('all', { date_from: monthAgoIso(), date_to: todayIso() }, 'xlsx');
      downloadBlobFile(file, 'all-reports-last-30-days.xlsx');
      setError(null);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Не удалось экспортировать отчеты в Excel');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 } }}>
      <Stack spacing={1.25}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight={900} sx={{ lineHeight: 1.1 }}>
            Отчеты
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Последние 30 дней: состояния, события, воспроизведения, контент и экспорт в Excel.
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <ReportsNavigation />

        <Grid container spacing={1}>
          <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
            <MetricCell
              icon={<MonitorHeart fontSize="small" />}
              label="Устройства онлайн"
              value={`${formatNumber(monitoring?.devices.online)}/${formatNumber(monitoring?.devices.total)}`}
              caption={formatPercent(monitoring?.devices.online_percentage)}
              progress={monitoring?.devices.online_percentage || 0}
              color={(monitoring?.devices.online_percentage || 0) >= 85 ? 'success' : 'warning'}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
            <MetricCell
              icon={<Assessment fontSize="small" />}
              label="Средний uptime"
              value={formatPercent(averageUptime)}
              caption={`${formatNumber(deviceUptime.length)} устройств в отчете`}
              progress={averageUptime}
              color={averageUptime >= 85 ? 'success' : averageUptime >= 60 ? 'warning' : 'error'}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
            <MetricCell
              icon={<PlayCircle fontSize="small" />}
              label="Воспроизведения"
              value={formatNumber(totalPlaybacks)}
              caption={formatDuration(totalPlaybackSeconds)}
              color={totalPlaybacks > 0 ? 'success' : 'warning'}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
            <MetricCell
              icon={<EventNote fontSize="small" />}
              label="События периода"
              value={formatNumber(totalEvents)}
              caption={`${formatNumber(errors)} ошибок`}
              color={errors > 0 ? 'error' : 'info'}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
            <MetricCell
              icon={<Download fontSize="small" />}
              label="Загрузки 24ч"
              value={formatNumber(monitoring?.content.uploads_24h)}
              caption={`${formatNumber(monitoring?.content.playbacks_24h)} показов 24ч`}
              color="info"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
            <MetricCell
              icon={<Assessment fontSize="small" />}
              label="Типы отчетов"
              value={formatNumber(reportTypes.length)}
              caption={`${filteredTypes.length} видно по фильтру`}
              color="primary"
            />
          </Grid>
        </Grid>

        <Paper sx={{ p: 1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
            <TextField
              size="small"
              placeholder="Поиск отчетов"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
              sx={{ minWidth: { xs: '100%', md: 300 } }}
            />
            <Button size="small" component={Link} to="/reports/proof-of-play" startIcon={<PlayCircle />} variant="outlined">
              Proof-of-Play
            </Button>
            <Button size="small" component={Link} to="/reports/content-statistics" startIcon={<Download />} variant="outlined">
              Контент
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" startIcon={<Download />} variant="contained" onClick={exportAllExcel} disabled={exporting}>
              Excel все отчеты
            </Button>
          </Stack>
        </Paper>

        <Grid container spacing={1}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Paper sx={{ p: 1, height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                <Typography variant="subtitle2" fontWeight={900}>Каталог отчетов</Typography>
                <Chip size="small" label={`${filteredTypes.length}/${reportTypes.length}`} />
              </Stack>
              {filteredTypes.length === 0 ? (
                <ReportEmptyState title="Отчеты не найдены" message="Измените поисковый запрос или проверьте доступность каталога отчетов." />
              ) : (
                <TableContainer sx={{ maxHeight: 340 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 180 }}>Отчет</TableCell>
                        <TableCell>Описание</TableCell>
                        <TableCell sx={{ width: 190 }}>Параметры</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredTypes.map((report) => (
                        <TableRow
                          key={report.type}
                          hover
                          component={Link}
                          to={routeByReportType[report.type] || '/reports'}
                          sx={{ textDecoration: 'none', '& td': { py: 0.65 } }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={800} noWrap>{report.name}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>{report.type}</Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={report.description} arrow>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 420 }} noWrap>
                                {report.description}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                              {report.parameters.slice(0, 3).map((parameter) => (
                                <Chip key={parameter} label={parameter} size="small" variant="outlined" sx={{ height: 20 }} />
                              ))}
                              {report.parameters.length > 3 && <Chip label={`+${report.parameters.length - 3}`} size="small" sx={{ height: 20 }} />}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 1, height: 214 }}>
                  <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>Низкий uptime</Typography>
                  <TableContainer sx={{ maxHeight: 176 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Устройство</TableCell>
                          <TableCell align="right">%</TableCell>
                          <TableCell>Статус</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {worstUptime.map((row) => (
                          <TableRow key={row.device_id} hover sx={{ '& td': { py: 0.55 } }}>
                            <TableCell><Typography variant="caption" noWrap>{row.device_name}</Typography></TableCell>
                            <TableCell align="right">{formatPercent(row.uptime_percentage)}</TableCell>
                            <TableCell><Chip size="small" label={row.device_status || 'unknown'} variant="outlined" sx={{ height: 20 }} /></TableCell>
                          </TableRow>
                        ))}
                        {worstUptime.length === 0 && (
                          <TableRow><TableCell colSpan={3}><Typography variant="caption" color="text.secondary">Нет данных.</Typography></TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 1, height: 214 }}>
                  <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>Топ контент</Typography>
                  <Stack spacing={0.65}>
                    {topContent.map((row) => (
                      <Box key={`${row.content_type}-${row.content_id}`}>
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <Typography variant="caption" noWrap>{row.content_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.total_plays}</Typography>
                        </Stack>
                        <LinearProgress variant="determinate" value={Math.min(100, row.completion_rate || 0)} sx={{ height: 4, borderRadius: 1 }} />
                      </Box>
                    ))}
                    {topContent.length === 0 && <Typography variant="caption" color="text.secondary">Нет воспроизведений.</Typography>}
                  </Stack>
                </Paper>
              </Grid>

              <Grid size={12}>
                <Paper sx={{ p: 1, height: 118 }}>
                  <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 0.75 }}>Активность по дням</Typography>
                  <Stack direction="row" spacing={0.6} alignItems="flex-end" sx={{ height: 70 }}>
                    {activityRows.map((row) => {
                      const max = Math.max(1, ...activityRows.map((item) => item.total_events));
                      return (
                        <Tooltip key={row.date} title={`${row.date}: ${row.total_events} событий, ${row.errors} ошибок`} arrow>
                          <Box sx={{ flex: 1, minWidth: 18 }}>
                            <Box
                              sx={{
                                height: `${Math.max(8, (row.total_events / max) * 52)}px`,
                                bgcolor: row.errors > 0 ? 'error.main' : 'primary.main',
                                borderRadius: 0.75,
                              }}
                            />
                            <Typography variant="caption" color="text.secondary" noWrap>{row.date.slice(5)}</Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                    {activityRows.length === 0 && <Typography variant="caption" color="text.secondary">Нет событий за период.</Typography>}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

export default ReportsPage;
